import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Target, X, Settings as SettingsIcon, User as UserIcon, Moon, Sun, Monitor, Mail, Bell, FileText, Users, Shield, BarChart3, Zap, ChevronLeft } from 'lucide-react';
import GoalsSettings from './GoalsSettings';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../contexts/AuthContext';

const sections = [
    { key: 'general', label: 'General', icon: SettingsIcon, description: 'Theme and basic preferences' },
    { key: 'goals', label: 'Goals & Targets', icon: Target, description: 'Business metrics and objectives' },
    { key: 'user', label: 'Profile', icon: UserIcon, description: 'Personal information and account' },
    { key: 'team', label: 'Team & Access', icon: Users, description: 'Member management and permissions' },
    { key: 'security', label: 'Security', icon: Shield, description: 'Privacy and security settings' },
];

const themeOptions = [
    { key: 'light', label: 'Light', icon: Sun, description: 'Clean and bright interface' },
    { key: 'dark', label: 'Dark', icon: Moon, description: 'Easy on the eyes' },
    { key: 'system', label: 'System', icon: Monitor, description: 'Match your device settings' },
];

const SimpleSettingsModal = ({ open, onClose }) => {
    const [activeSection, setActiveSection] = useState('goals');
    const [hasChanges, setHasChanges] = useState(false);
    const { theme, setThemeMode } = useTheme();
    const { user } = useAuth();
    const [editEmail, setEditEmail] = useState(user?.email || '');
    const [editName, setEditName] = useState(user?.displayName || '');
    const [emailNotifications, setEmailNotifications] = useState(true);

    // Resume Builder Admin Settings
    const [resumeSettings, setResumeSettings] = useState({
        templates: {
            enabled: ['modern', 'classic', 'creative', 'minimal'],
            default: 'modern',
            customBranding: true
        },
        fields: {
            required: ['name', 'email', 'experience'],
            optional: ['skills', 'certifications', 'projects'],
            maxResumes: 50
        },
        analytics: {
            trackUsage: true,
            exportData: true,
            retention: 90
        }
    });

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    useEffect(() => {
        setEditEmail(user?.email || '');
        setEditName(user?.displayName || '');
    }, [user]);

    const saveChanges = () => {
        // Save logic here
        setHasChanges(false);
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] bg-background">
            {/* Mobile Full-Screen Layout */}
            <div className="block lg:hidden">
                {/* Mobile Header */}
                <div className="flex items-center justify-between p-4 border-b border-border/50 bg-background/95 backdrop-blur-sm sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold">Settings</h2>
                            <p className="text-sm text-muted-foreground">Manage your workspace</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Mobile Tab Navigation */}
                <div className="border-b border-border/50 bg-background/95 backdrop-blur-sm sticky top-[73px] z-10">
                    <div className="flex overflow-x-auto scrollbar-hide">
                        {sections.map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap border-b-2 transition-all ${activeSection === key
                                    ? 'border-primary text-primary bg-primary/5'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                    }`}
                                onClick={() => setActiveSection(key)}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="text-sm font-medium">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
                        {activeSection === 'general' && (
                            <div className="space-y-6">
                                {/* Theme Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Moon className="w-5 h-5 text-primary" />
                                        <h3 className="text-lg font-semibold">Appearance</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {themeOptions.map(({ key, label, icon: Icon, description }) => (
                                            <button
                                                key={key}
                                                className={`flex flex-col items-center gap-3 p-4 rounded-xl border transition-all ${theme === key
                                                    ? 'bg-primary/10 border-primary/50 text-primary shadow-lg'
                                                    : 'bg-card border-border hover:border-primary/30 hover:bg-primary/5'
                                                    }`}
                                                onClick={() => setThemeMode(key)}
                                            >
                                                <Icon className="h-6 w-6" />
                                                <div className="text-center">
                                                    <div className="font-medium text-sm">{label}</div>
                                                    <div className="text-xs text-muted-foreground mt-1">{description}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'goals' && <GoalsSettings />}

                        {activeSection === 'user' && (
                            <div className="space-y-6">
                                {/* Profile Header */}
                                <div className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border">
                                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                                        <UserIcon className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">{editName || 'User'}</h3>
                                        <p className="text-muted-foreground">{editEmail}</p>
                                        <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full mt-2">
                                            Admin
                                        </span>
                                    </div>
                                </div>

                                {/* Profile Settings */}
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <h4 className="font-semibold flex items-center gap-2">
                                            <Mail className="w-5 h-5 text-primary" />
                                            Contact Information
                                        </h4>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block font-medium mb-2" htmlFor="user-email">
                                                    Email Address
                                                </label>
                                                <input
                                                    id="user-email"
                                                    type="email"
                                                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                    value={editEmail}
                                                    onChange={e => setEditEmail(e.target.value)}
                                                    disabled
                                                />
                                                <p className="text-xs text-muted-foreground mt-2">Contact your administrator to change your email address</p>
                                            </div>

                                            <div>
                                                <label className="block font-medium mb-2" htmlFor="user-name">
                                                    Display Name
                                                </label>
                                                <input
                                                    id="user-name"
                                                    type="text"
                                                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                    value={editName}
                                                    onChange={e => setEditName(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notification Settings */}
                                    <div className="space-y-4">
                                        <h4 className="font-semibold flex items-center gap-2">
                                            <Bell className="w-5 h-5 text-primary" />
                                            Notifications
                                        </h4>

                                        <div className="space-y-3">
                                            <label className="flex items-center justify-between p-3 bg-card rounded-lg border border-border cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    <Mail className="w-5 h-5 text-primary" />
                                                    <div>
                                                        <div className="font-medium text-sm">Email Notifications</div>
                                                        <div className="text-xs text-muted-foreground">Receive updates via email</div>
                                                    </div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={emailNotifications}
                                                    onChange={e => setEmailNotifications(e.target.checked)}
                                                    className="w-4 h-4 text-primary rounded"
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'team' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-primary" />
                                        <h3 className="text-lg font-semibold">Team Management</h3>
                                    </div>
                                    <button className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm">
                                        Invite Member
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {[
                                        { name: 'John Doe', email: 'john@company.com', role: 'Admin', status: 'Active' },
                                        { name: 'Jane Smith', email: 'jane@company.com', role: 'Editor', status: 'Active' },
                                        { name: 'Mike Johnson', email: 'mike@company.com', role: 'Viewer', status: 'Pending' }
                                    ].map((member, index) => (
                                        <div key={index} className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                                    <UserIcon className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{member.name}</p>
                                                    <p className="text-sm text-muted-foreground">{member.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-2 py-1 text-xs rounded-full ${member.status === 'Active'
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                    }`}>
                                                    {member.status}
                                                </span>
                                                <select className="px-3 py-1 bg-background border border-border rounded text-sm">
                                                    <option>{member.role}</option>
                                                    <option>Admin</option>
                                                    <option>Editor</option>
                                                    <option>Viewer</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeSection === 'security' && (
                            <div className="space-y-6">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-primary" />
                                        <h3 className="text-lg font-semibold">Security & Privacy</h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="p-4 bg-card rounded-lg border border-border">
                                            <h4 className="font-medium mb-3">Two-Factor Authentication</h4>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm">Add an extra layer of security</p>
                                                    <p className="text-xs text-muted-foreground">Recommended for admin accounts</p>
                                                </div>
                                                <button className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors">
                                                    Enable
                                                </button>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-card rounded-lg border border-border">
                                            <h4 className="font-medium mb-3">Session Management</h4>
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm">Auto-logout after inactivity</span>
                                                    <input type="checkbox" className="w-4 h-4 text-primary rounded" defaultChecked />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm">Remember login for 30 days</span>
                                                    <input type="checkbox" className="w-4 h-4 text-primary rounded" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Changes Footer */}
                {hasChanges && (
                    <div className="p-4 border-t border-border bg-background/95 backdrop-blur-sm sticky bottom-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                You have unsaved changes
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setHasChanges(false)}
                                    className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                                >
                                    Discard
                                </button>
                                <button
                                    onClick={saveChanges}
                                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop/iPad Notion-Style Popup */}
            <div className="hidden lg:flex items-center justify-center min-h-screen p-4">
                <div className="bg-background/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-border/50">
                        <div>
                            <h2 className="text-xl font-semibold">Settings</h2>
                            <p className="text-sm text-muted-foreground">Manage your workspace preferences</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex h-[calc(85vh-120px)]">
                        {/* Sidebar */}
                        <aside className="w-64 border-r border-border/50 bg-muted/20">
                            <div className="p-4">
                                <nav className="space-y-1">
                                    {sections.map(({ key, label, icon: Icon, description }) => (
                                        <button
                                            key={key}
                                            className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all ${activeSection === key
                                                    ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                                }`}
                                            onClick={() => setActiveSection(key)}
                                        >
                                            <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <div className="font-medium text-sm">{label}</div>
                                                <div className="text-xs text-muted-foreground mt-1">{description}</div>
                                            </div>
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <main className="flex-1 overflow-y-auto bg-background/50">
                            <div className="p-6">
                                {activeSection === 'general' && (
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <Moon className="w-5 h-5 text-primary" />
                                                <h3 className="text-lg font-semibold">Appearance</h3>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                {themeOptions.map(({ key, label, icon: Icon, description }) => (
                                                    <button
                                                        key={key}
                                                        className={`flex flex-col items-center gap-3 p-4 rounded-xl border transition-all ${theme === key
                                                            ? 'bg-primary/10 border-primary/50 text-primary shadow-lg'
                                                            : 'bg-card/50 border-border/50 hover:border-primary/30 hover:bg-primary/5'
                                                            }`}
                                                        onClick={() => setThemeMode(key)}
                                                    >
                                                        <Icon className="h-6 w-6" />
                                                        <div className="text-center">
                                                            <div className="font-medium text-sm">{label}</div>
                                                            <div className="text-xs text-muted-foreground mt-1">{description}</div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeSection === 'goals' && <GoalsSettings />}

                                {activeSection === 'user' && (
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-4 p-4 bg-card/50 rounded-xl border border-border/50">
                                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                                                <UserIcon className="w-8 h-8 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg">{editName || 'User'}</h3>
                                                <p className="text-muted-foreground">{editEmail}</p>
                                                <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full mt-2">
                                                    Admin
                                                </span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <h4 className="font-semibold flex items-center gap-2">
                                                    <Mail className="w-5 h-5 text-primary" />
                                                    Contact Information
                                                </h4>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block font-medium mb-2" htmlFor="user-email">
                                                            Email Address
                                                        </label>
                                                        <input
                                                            id="user-email"
                                                            type="email"
                                                            className="w-full px-4 py-3 rounded-lg border border-border/50 bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                            value={editEmail}
                                                            onChange={e => setEditEmail(e.target.value)}
                                                            disabled
                                                        />
                                                        <p className="text-xs text-muted-foreground mt-2">Contact your administrator to change your email address</p>
                                                    </div>

                                                    <div>
                                                        <label className="block font-medium mb-2" htmlFor="user-name">
                                                            Display Name
                                                        </label>
                                                        <input
                                                            id="user-name"
                                                            type="text"
                                                            className="w-full px-4 py-3 rounded-lg border border-border/50 bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                            value={editName}
                                                            onChange={e => setEditName(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <h4 className="font-semibold flex items-center gap-2">
                                                    <Bell className="w-5 h-5 text-primary" />
                                                    Notifications
                                                </h4>

                                                <div className="space-y-3">
                                                    <label className="flex items-center justify-between p-3 bg-card/50 rounded-lg border border-border/50 cursor-pointer">
                                                        <div className="flex items-center gap-3">
                                                            <Mail className="w-5 h-5 text-primary" />
                                                            <div>
                                                                <div className="font-medium text-sm">Email Notifications</div>
                                                                <div className="text-xs text-muted-foreground">Receive updates via email</div>
                                                            </div>
                                                        </div>
                                                        <input
                                                            type="checkbox"
                                                            checked={emailNotifications}
                                                            onChange={e => setEmailNotifications(e.target.checked)}
                                                            className="w-4 h-4 text-primary rounded"
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeSection === 'team' && (
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-5 h-5 text-primary" />
                                                <h3 className="text-lg font-semibold">Team Management</h3>
                                            </div>
                                            <button className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm">
                                                Invite Member
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {[
                                                { name: 'John Doe', email: 'john@company.com', role: 'Admin', status: 'Active' },
                                                { name: 'Jane Smith', email: 'jane@company.com', role: 'Editor', status: 'Active' },
                                                { name: 'Mike Johnson', email: 'mike@company.com', role: 'Viewer', status: 'Pending' }
                                            ].map((member, index) => (
                                                <div key={index} className="flex items-center justify-between p-4 bg-card/50 rounded-lg border border-border/50">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                                            <UserIcon className="w-5 h-5 text-muted-foreground" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{member.name}</p>
                                                            <p className="text-sm text-muted-foreground">{member.email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${member.status === 'Active'
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                            }`}>
                                                            {member.status}
                                                        </span>
                                                        <select className="px-3 py-1 bg-background/50 border border-border/50 rounded text-sm">
                                                            <option>{member.role}</option>
                                                            <option>Admin</option>
                                                            <option>Editor</option>
                                                            <option>Viewer</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeSection === 'security' && (
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-5 h-5 text-primary" />
                                                <h3 className="text-lg font-semibold">Security & Privacy</h3>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-4 bg-card/50 rounded-lg border border-border/50">
                                                    <h4 className="font-medium mb-3">Two-Factor Authentication</h4>
                                                    <div className="space-y-3">
                                                        <p className="text-sm">Add an extra layer of security</p>
                                                        <p className="text-xs text-muted-foreground">Recommended for admin accounts</p>
                                                        <button className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors">
                                                            Enable
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="p-4 bg-card/50 rounded-lg border border-border/50">
                                                    <h4 className="font-medium mb-3">Session Management</h4>
                                                    <div className="space-y-3">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm">Auto-logout after inactivity</span>
                                                            <input type="checkbox" className="w-4 h-4 text-primary rounded" defaultChecked />
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm">Remember login for 30 days</span>
                                                            <input type="checkbox" className="w-4 h-4 text-primary rounded" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>

                    {/* Save Changes Footer */}
                    {hasChanges && (
                        <div className="p-4 border-t border-border/50 bg-background/95 backdrop-blur-sm">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                    You have unsaved changes
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setHasChanges(false)}
                                        className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                                    >
                                        Discard
                                    </button>
                                    <button
                                        onClick={saveChanges}
                                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SimpleSettingsModal;